import { SvgIcon } from "@mui/material";
import { getDurationAriaLabelFromMs } from "../../../../../helpers/time";
import i18n from "../../../../../i18n";
import { forwardRef, ReactNode, useRef, useState } from "react";
import { CoreIconButton } from "@verity/components/core";
import styles from "./VideoControls.module.css";
import LowVolumeIcon from "../../../Resources/speaker-simple-low.svg?react";
import HighVolumeIcon from "../../../Resources/speaker-simple-high.svg?react";
import MutedIcon from "../../../Resources/muted-icon.svg?react";

interface VideoControlButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
	children: ReactNode;
	className?: string;
	"aria-label": string;
	onClick?: React.MouseEventHandler<HTMLElement>;
}

export const VideoControlButton = forwardRef<HTMLButtonElement, VideoControlButtonProps>(
	function VideoControlButton(props, ref) {
		const { children, className, onClick, "aria-label": ariaLabel, ...rest } = props;
		return (
			<CoreIconButton
				className={className}
				onClick={onClick}
				aria-label={ariaLabel}
				ref={ref}
				variant="plain"
				sx={{
					"&:active": {
						backgroundColor: "#333333",
					},
					"&:hover": {
						backgroundColor: "#484848 !important", // Override corebuttonicon hover effect
					},
				}}
				{...rest}
			>
				{children}
			</CoreIconButton>
		);
	},
);

interface VolumeControlsProps {
	inFullscreen: boolean;
	onVolumeButtonClick?: () => void;
	volume: number;
	onVolumeInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
	showVolumeInput: boolean;
}

export function VolumeControls({
	inFullscreen,
	onVolumeButtonClick,
	volume,
	onVolumeInputChange,
	showVolumeInput,
}: VolumeControlsProps) {
	// Handles the display of the volume bar
	const [volumeInputShowing, setVolumeInputShowing] = useState(false);

	// Delay closing the volume slider on blur to allow focus to move
	// to the slider input (e.g. when tabbing from the volume button).
	// Without the timeout, the blur fires before the next focus,
	// causing the slider to hide too soon.
	const volumeBlurTimeoutRef = useRef<number | null>(null);

	const handleFocusWithin = () => {
		if (volumeBlurTimeoutRef.current !== null) {
			clearTimeout(volumeBlurTimeoutRef.current);
			volumeBlurTimeoutRef.current = null;
		}

		setVolumeInputShowing(true);
	};
	const handleBlurWithin = () => {
		volumeBlurTimeoutRef.current = window.setTimeout(() => {
			setVolumeInputShowing(false);
		}, 100);
	};

	const volumeIconSx = {
		color: "white",
		fontSize: inFullscreen ? "32px" : "22px",
		fill: "none",
	};

	const volumeSliderColorAmount = volume * 100;
	const sliderTrackStyle = {
		background:
			"linear-gradient(to right, #fff 0%, #fff " +
			volumeSliderColorAmount.toString() +
			"%, #676767 " +
			volumeSliderColorAmount.toString() +
			"%, #676767 100%)",
	};
	const fullScreenStyleSuffix = inFullscreen ? "_fs" : "";
	return (
		<div className={styles[`volume_wrap${fullScreenStyleSuffix}`]} style={{ flex: volumeInputShowing ? 1 : 0 }}>
			{showVolumeInput && (
				<div
					className={styles[`volumehover${fullScreenStyleSuffix}`]}
					onMouseEnter={() => setVolumeInputShowing(true)}
					onMouseLeave={() => setVolumeInputShowing(false)}
					onFocus={handleFocusWithin}
					onBlur={handleBlurWithin}
				>
					<VideoControlButton
						onClick={onVolumeButtonClick}
						aria-label={
							volume === 0
								? i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.ariaLabels.unmute")
								: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.ariaLabels.mute")
						}
						aria-haspopup="true"
					>
						<SvgIcon
							component={volume >= 0.5 ? HighVolumeIcon : volume === 0 ? MutedIcon : LowVolumeIcon}
							sx={volumeIconSx}
						/>
					</VideoControlButton>

					<div
						className={styles[`volumebar_wrapper${fullScreenStyleSuffix}`]}
						style={{
							overflow: volumeInputShowing ? "visible" : "hidden",
							visibility: volumeInputShowing ? "visible" : "hidden",
						}}
					>
						<input
							aria-label={i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.ariaLabels.volume")}
							className={styles[`volumebar${fullScreenStyleSuffix}`]}
							type="range"
							value={volume}
							tabIndex={volumeInputShowing ? 0 : -1}
							min={0}
							max={1}
							step={0.01}
							style={sliderTrackStyle}
							onChange={onVolumeInputChange}
						/>
					</div>
				</div>
			)}
		</div>
	);
}

interface SeekbarProps {
	inFullscreen: boolean;
	curSeekbarValue: number;
	maxSeekbarValue: number | undefined;
	minSeekbarValue: number;
	onSeekbarChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
	children?: React.ReactNode;
	timeOffset?: number;
}

export function Seekbar({
	inFullscreen,
	curSeekbarValue,
	maxSeekbarValue,
	minSeekbarValue,
	onSeekbarChange,
	timeOffset = 0,
	children,
}: SeekbarProps) {
	const colorAmount = ((curSeekbarValue - timeOffset) / ((maxSeekbarValue ?? 0) - timeOffset)) * 100;
	const seekbarSliderColor = {
		background:
			"linear-gradient(to right, #fff 0%, #fff " +
			colorAmount.toString() +
			"%, #676767 " +
			colorAmount.toString() +
			"%, #676767 100%)",
	};

	const fullScreenStyleSuffix = inFullscreen ? "_fs" : "";
	return (
		<div className={styles[`padding_wrap${fullScreenStyleSuffix}`]}>
			<div className={styles[`seek_bar_wrap${fullScreenStyleSuffix}`]}>
				<input
					className={styles[`seekbar${fullScreenStyleSuffix}`]}
					id="seekbar_Video"
					type="range"
					value={curSeekbarValue}
					style={seekbarSliderColor}
					min={minSeekbarValue}
					max={maxSeekbarValue}
					aria-label={i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.ariaLabels.seekSlider")}
					aria-valuemin={0} // For screen readers, tell user that video starts at 0 seconds
					aria-valuemax={maxSeekbarValue ? Math.round(maxSeekbarValue / 1000) : 0} // Convert to second
					aria-valuenow={Math.round(curSeekbarValue / 1000)}
					aria-valuetext={getDurationAriaLabelFromMs(curSeekbarValue)}
					onChange={onSeekbarChange}
				/>
				{children}
			</div>
		</div>
	);
}
